// glide-ui: 5
//
// Progress — horizontal progress bar. Pass value 0–100 (clamped).
// props: value (number, 0-100, clamped), variant? ("default"|"success"|"warning"|"error"), + Radix Progress.Root props
// always pass aria-label or aria-labelledby
// <Progress value={60} aria-label="Upload" />   <Progress value={100} variant="success" aria-label="Done" />

import React, { forwardRef } from "react";
import * as ProgressPrimitive from "@radix-ui/react-progress";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@glide/cn";

const trackVariants = cva("relative h-2 w-full overflow-hidden rounded-full", {
  variants: {
    variant: {
      default: "bg-accent/20",
      success: "bg-success-weak",
      warning: "bg-warning-weak",
      error: "bg-error-weak",
    },
  },
  defaultVariants: { variant: "default" },
});

const indicatorVariants = cva("h-full w-full flex-1 transition-all", {
  variants: {
    variant: {
      default: "bg-accent",
      success: "bg-success",
      warning: "bg-warning",
      error: "bg-error",
    },
  },
  defaultVariants: { variant: "default" },
});

export interface ProgressProps
  extends
    Omit<
      React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root>,
      "value"
    >,
    VariantProps<typeof trackVariants> {
  /** 0–100. Values outside the range are clamped. Undefined/null = 0. */
  value?: number | null;
}

export const Progress = forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  ProgressProps
>(({ className, value, variant, ...props }, ref) => {
  const clamped = Math.min(100, Math.max(0, value ?? 0));
  return (
    <ProgressPrimitive.Root
      ref={ref}
      data-slot="progress"
      value={clamped}
      className={cn(trackVariants({ variant }), className)}
      {...props}
    >
      <ProgressPrimitive.Indicator
        data-slot="progress-indicator"
        className={indicatorVariants({ variant })}
        style={{ transform: `translateX(-${100 - clamped}%)` }}
      />
    </ProgressPrimitive.Root>
  );
});
Progress.displayName = "Progress";
